import { useUser } from "@/zustand/store";
import {
  ArrowDownLeft,
  ArrowUpDown,
  ArrowUpRight,
  Eye,
  EyeOff,
  PlusCircle,
  Share2,
} from "lucide-react";
import {
  useGetCards,
  useGetCardTransactions,
  useGetRecentTransactions,
} from "@/hooks/use-queries";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import Card from "@/components/dashboard-card";
import { DottedBorderBox } from "@/components/dotted-border";
import Copy from "@/components/copy";
import { DataTable } from "@/components/data-table";
import { columns } from "./_misc/columns";
import TopBar from "@/components/topbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { useSheetStore } from "@/zustand/sheetStore";
import { useModalStore } from "@/zustand/modalStore";
import TransactionList from "./_misc/TransactionList";
import CardSkeleton from "@/components/skeletons/card-skeleton";
import { cardColumns } from "./_misc/cardColumns";
import { handleShare } from "@/lib/helper";
import { useFormatAmountWithCurrency } from "@/hooks/use-format-with-currency";

const Dashboard = () => {
  const navigate = useNavigate();
  const { openModal } = useModalStore();
  const { openSheet } = useSheetStore();
  const formatAmount = useFormatAmountWithCurrency();

  const [paginationParams, setPaginationParams] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [pageCount, setPageCount] = useState(1);
  const [tab, setTab] = useState<"wallet" | "cards">("wallet");
  const { user } = useUser((state: any) => state);
  const { data: cards, isLoading } = useGetCards();
  const { data: transactions, isLoading: isLoadingTransactions } =
    useGetRecentTransactions();
  const [showBalance, setShowBalance] = useState(true);

  const { data: cardTransactions, isLoading: isCardLoading } =
    useGetCardTransactions({
      page: 0,
      limit: 150,
    });

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div>
      {/* header */}
      <TopBar title={`Hello ${user?.firstName}, 👋🏻`} />

      <div className="relative px-4">
        <img
          src="/images/bg-logo.png"
          alt=""
          className="fixed right-1/2 bottom-[calc(50%-100px)] z-0 translate-x-1/2 translate-y-1/2 lg:right-2/5"
        />

        <div
          className={`relative mt-6 grid w-full grid-cols-1 gap-2 lg:gap-4 ${user?.canRefer ? "lg:grid-cols-3" : "lg:grid-cols-2"} `}
        >
          <div className="col-span-2 grid gap-2 lg:col-span-2 lg:grid-cols-4 lg:gap-4">
            <div
              className={`thin-scrollbar relative h-full overflow-hidden overflow-x-auto rounded-2xl p-4 lg:col-span-2`}
              style={{
                background:
                  "linear-gradient(180deg, rgba(251, 254, 255, 0.276) 0%, rgba(222, 218, 229, 0.069) 100%)",
                backdropFilter: "blur(50px)",
                boxShadow: "0px 4px 17px -1px #BDE9FB33",
              }}
            >
              <img
                src="/images/grain-texture.jpg"
                alt=""
                className="absolute inset-0 z-0 h-full w-full object-cover opacity-2"
              />
              <div className="mb-4 flex items-center justify-between text-white">
                <span>My Cards</span>
                <button
                  className="flex size-6 items-center justify-center rounded-full"
                  style={{
                    background:
                      "linear-gradient(128.62deg, #9191CB66 11.02%, #EDD6FF66 93.11%)",
                  }}
                >
                  <ArrowUpRight className="text-primary-100 size-3" />
                </button>
              </div>
              <div
                className={`relative mt-4 flex gap-4 text-white ${cards?.data.length === 1 || cards?.data.length === 0 || cards?.data.length == undefined ? "justify-center" : ""}`}
              >
                {isLoading ? (
                  <CardSkeleton />
                ) : cards?.data.length === 0 ? (
                  <div className="flex items-center justify-center">
                    <div className="flex flex-col text-center">
                      <h2 className="text-2xl font-semibold">No Cards</h2>
                      <p className="max-w-50 text-sm leading-4 font-normal">
                        Instantly create a card to start making transactions
                      </p>
                      <div className="mt-4">
                        <Button
                          className="text-primary-500 bg-primary-100 hover:bg-primary-100-hover h-11 w-full py-1.5"
                          onClick={() => {
                            openSheet("createCard", 2, {}, false);
                          }}
                        >
                          <PlusCircle className="size-4" />
                          Buy Card
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`thin-scrollbar relative flex w-full gap-4 overflow-x-auto pb-2 ${cards?.data.length === 1 ? "justify-center" : ""}`}
                  >
                    {cards?.data.map((_, index) => (
                      <Card
                        className={
                          "inset-0 h-40 shadow-[rgba(0,_0,_0,_0.25)_0px_25px_50px_-12px] hover:cursor-pointer hover:backdrop-blur-sm"
                        }
                        key={index}
                        card={cards?.data[index]}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="col-start-1 row-end-2 flex flex-col lg:col-span-2 lg:col-start-3 lg:space-y-2">
              <div
                className="font-dm-sans relative box-border flex w-full items-center gap-4 rounded-2xl bg-[linear-gradient(104.01deg,#1F2021_2.88%,#3A3B40_99.58%)] p-4 text-white lg:h-full lg:flex-col lg:items-start lg:justify-between"
                style={{
                  background:
                    "linear-gradient(104.01deg, #292934 2.88%, #454351 99.58%)",
                }}
              >
                <DottedBorderBox strokeColor={"#9191CB"} strokeWidth={0.5} />
                <div className="flex justify-between lg:w-full">
                  <img
                    src="/icons/gradient-wallet.svg"
                    className="size-8"
                    alt="wallet icon"
                  />

                  <button
                    onClick={() => navigate("/dashboard/wallet")}
                    className="hover:bg-primary-500/20 text-primary-100 relative hidden size-4 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full lg:flex"
                    style={{
                      background:
                        "linear-gradient(128.62deg, #9191CB66 11.02%, #EDD6FF66 93.11%)",
                    }}
                  >
                    <ArrowUpRight className="size-3" />
                  </button>
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Wallet Balance</span>
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        setShowBalance(!showBalance);
                      }}
                      variant={"ghost"}
                      className="size-auto cursor-pointer p-1! hover:bg-white/10"
                    >
                      {showBalance ? (
                        <EyeOff className="size-4 text-white/40" />
                      ) : (
                        <Eye className="size-4 text-white/40" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xl font-medium">
                    {showBalance ? `${formatAmount(user?.balance)}` : "****"}
                  </p>
                </div>
              </div>

              <div
                className="font-dm-sans hidden justify-between space-x-1.5 rounded-2xl border border-[#8ECAEC]/30 px-3 py-3 *:grow lg:flex"
                style={{
                  background:
                    "linear-gradient(104.01deg, rgba(38, 38, 38, 0.95) 2.88%, rgba(47, 48, 50, 0.95) 99.58%)",
                }}
              >
                <Button
                  onClick={() => openModal("walletDeposit")}
                  className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
                >
                  <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                    <ArrowDownLeft className="size-4" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs font-medium">Deposit</p>
                </Button>
                <Button
                  onClick={() => openSheet("withdraw", 2)}
                  className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
                >
                  <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                    <ArrowUpRight className="size-4" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs font-medium">Withdraw</p>
                </Button>
                <Button
                  onClick={() => openModal("transfer")}
                  className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
                >
                  <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                    <ArrowUpDown
                      className="size-4 rotate-45"
                      strokeWidth={1.5}
                    />
                  </div>
                  <p className="text-xs font-medium">Transfer</p>
                </Button>
              </div>
            </div>
          </div>
          {user?.canRefer && (
            <div
              className="col-span-2 hidden w-full grid-cols-2 flex-col justify-between gap-4 rounded-2xl bg-linear-(--gradient-light-blue) px-4 py-4 text-white lg:col-span-1 lg:flex"
              style={{
                background:
                  "linear-gradient(123.04deg, #9BBBFF 1.64%, #505E76 98.52%)",
              }}
            >
              {/* total commission */}
              <div className="flex items-center justify-between">
                <img
                  className="size-8 shrink-0"
                  src="/icons/gradient-users.svg"
                  alt="Total Commission"
                />
                <div className="text-right font-medium">
                  <p className="text-xs">Total Commission</p>
                  <p className="mt-2 text-xl leading-2">
                    {formatAmount(user?.totalCommission)}
                  </p>
                </div>
              </div>
              <div className="mt-6">
                <p className="text-sm leading-4">
                  Refer friends to earn commission rewards.
                </p>
                <div className="mt-2 font-medium">
                  <p className="text-xs">Commission Rate</p>
                  <p className="mt-2 flex items-center gap-4 leading-2">
                    <span>
                      25 <span className="text-[10px]">%(Deposit)</span>
                    </span>{" "}
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-2.5 *:w-1/2">
                  <div
                    className={
                      "flex h-10 items-center justify-between rounded-md px-2.5"
                    }
                    style={{
                      background:
                        "linear-gradient(129.49deg, rgba(41, 41, 52, 0.9) 3.6%, rgba(72, 72, 93, 0.9) 100%)",
                    }}
                  >
                    <span className="w-5/6 overflow-hidden text-ellipsis whitespace-nowrap">
                      {user?.id}
                    </span>
                    <Copy text={user?.id} icon="/icons/copy2.svg" side="left" />
                  </div>
                  <Button
                    onClick={() => handleShare(`signup?ref=${user?.id}`)}
                    className={
                      "bg-primary-100 hover:bg-primary-100/80 text-primary-600 flex h-10 items-center justify-center gap-1 rounded-md hover:cursor-pointer"
                    }
                  >
                    <Share2 className="text-primary-600 size-4" />
                    <span>Share Your Link</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="font-dm-sans col-span-2 flex w-full justify-between space-x-1.5 rounded-2xl border border-[#8ECAEC]/30 bg-[linear-gradient(104.01deg,#1F2021_2.88%,#3A3B40_99.58%)] px-3 py-2 *:grow lg:hidden">
            <Button
              onClick={() => openModal("walletDeposit")}
              className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
            >
              <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                <ArrowDownLeft className="size-4" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-medium">Deposit</p>
            </Button>
            <Button
              onClick={() => openSheet("withdraw", 2)}
              className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
            >
              <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                <ArrowUpRight className="size-4" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-medium">Withdraw</p>
            </Button>
            <Button
              onClick={() => openModal("transfer")}
              className="hover:text-primary-500 hover:bg-primary-100 flex h-auto flex-col items-center justify-between rounded-md bg-transparent px-2 py-1.5 text-white transition-all duration-300"
            >
              <div className="text-primary-500 bg-primary-100 flex size-7 items-center justify-center rounded-full border border-[#4D6AAE]">
                <ArrowUpDown className="size-4 rotate-45" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-medium">Transfer</p>
            </Button>
          </div>
        </div>

        <div className="bg-bg-primary/90 mt-4 rounded-2xl p-4 text-white backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Recent Transactions</h2>
            <Link
              to="/dashboard/transactions"
              className="text-primary-100 text-xs hover:underline"
            >
              View All
            </Link>
          </div>
          <Tabs
            onValueChange={(value: "wallet" | "cards") => {
              setTab(value);
            }}
            value={tab}
            className="bg-transparent py-4"
          >
            <TabsList className="w-full border border-[#746e82] bg-white/10 p-0 lg:w-fit">
              <TabsTrigger
                value="wallet"
                className="data-[state=active]:text-primary-500 rounded-l-lg rounded-r-none px-6 text-[#E9F9FF] data-[state=active]:bg-[linear-gradient(128.62deg,#9191CB_11.02%,#EDD6FF_93.11%)]"
              >
                Wallet
              </TabsTrigger>
              <TabsTrigger
                value="cards"
                className="data-[state=active]:text-primary-500 rounded-l-none rounded-r-lg px-6 text-[#E9F9FF] data-[state=active]:bg-[linear-gradient(128.62deg,#9191CB_11.02%,#EDD6FF_93.11%)]"
              >
                Cards
              </TabsTrigger>
            </TabsList>
            <TabsContent value="wallet" className="mt-2">
              <div className="hidden lg:block">
                <DataTable
                  columns={columns}
                  data={transactions ?? []}
                  isLoading={isLoadingTransactions}
                />
              </div>
              <div className="lg:hidden">
                <TransactionList
                  isLoading={isLoadingTransactions}
                  transactions={transactions ?? []}
                />
              </div>
            </TabsContent>
            <TabsContent value="cards" className="mt-2">
              <div className="hidden lg:block">
                <DataTable
                  columns={cardColumns}
                  data={cardTransactions?.data.slice(0, 10) ?? []}
                  isLoading={isCardLoading}
                />
              </div>
              <div className="lg:hidden">
                <TransactionList
                  type="cards"
                  isLoading={isCardLoading}
                  transactions={cardTransactions?.data.slice(0, 10) ?? []}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
